import { createClient } from '@supabase/supabase-js'

import { SUPABASE_ANON_KEY, SUPABASE_URL } from './env'
import type { Database } from './database.types'

/**
 * The one Supabase client for the app.
 *
 * A single instance matters: each `createClient` call installs its own auth
 * listener and refresh timer, so duplicates cause double token refreshes and
 * sessions that fight each other across tabs.
 */
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // The app is not an OAuth callback target, so URL-based session detection
    // only risks mis-parsing our own query strings (report filters live there).
    detectSessionInUrl: false,
    storageKey: 'cbs.auth',
    flowType: 'pkce',
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-application-name': 'company-balance-sheet',
    },
  },
})

/** Postgres error codes worth handling by name rather than by message text. */
export const PG_ERROR = {
  /** RLS denied the operation, or the row simply is not visible to this user. */
  INSUFFICIENT_PRIVILEGE: '42501',
  UNIQUE_VIOLATION: '23505',
  FOREIGN_KEY_VIOLATION: '23503',
  CHECK_VIOLATION: '23514',
  NOT_NULL_VIOLATION: '23502',
  RAISE_EXCEPTION: 'P0001',
  /** Our own caps and throttles: the 10-company limit, the invitation rate limit. */
  PROGRAM_LIMIT_EXCEEDED: '54000',
  /**
   * The schema is behind the code — a column or function this build needs does
   * not exist yet. In a migration-driven repo this means exactly one thing, so
   * it is worth naming rather than surfacing as "something went wrong".
   */
  UNDEFINED_COLUMN: '42703',
  UNDEFINED_FUNCTION: '42883',
  UNDEFINED_TABLE: '42P01',
} as const

export interface AppError {
  message: string
  code?: string | undefined
  /** True when retrying the same request might succeed. */
  retryable: boolean
}

/**
 * Turns a Supabase/PostgREST error into something a person can act on.
 *
 * Raw Postgres errors leak constraint names and SQL fragments into the UI, which
 * is both ugly and a small information leak about the schema.
 */
/**
 * Postgres's own wording for a tripped constraint. These read like
 * `new row for relation "payments" violates check constraint "payments_amount_check"`
 * — accurate, and useless to anyone who is not holding the schema.
 */
const RAW_CONSTRAINT =
  /violates (check|not-null|foreign key|unique) constraint|null value in column/i

/**
 * The message a plpgsql function WROTE for the user, or null if Postgres wrote it.
 *
 * Several RPCs raise deliberate errors on constraint SQLSTATEs rather than on
 * P0001 — `create_payment` uses `using errcode = '23514'` for "a payment needs a
 * contact". Keying the message purely off the SQLSTATE therefore threw away the
 * only sentence that told the user what to fix. There is no flag distinguishing
 * the two, so the raw Postgres phrasing is what gets matched: anything that does
 * not look like it came from the constraint machinery was authored for a human.
 */
function authoredMessage(err: { code?: string; message?: string }): AppError | null {
  const message = err.message?.trim()
  if (!message || RAW_CONSTRAINT.test(message)) return null
  return { message, code: err.code, retryable: false }
}

export function toAppError(error: unknown): AppError {
  if (error === null || error === undefined) {
    return { message: 'Something went wrong.', retryable: true }
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { message: 'You are offline. Check your connection and try again.', retryable: true }
  }

  const err = error as { code?: string; message?: string; details?: string; hint?: string }
  const code = err.code

  switch (code) {
    case PG_ERROR.INSUFFICIENT_PRIVILEGE:
      return {
        message: 'You do not have permission to do that.',
        code,
        retryable: false,
      }

    case PG_ERROR.UNIQUE_VIOLATION:
      return {
        message: 'A record with these details already exists.',
        code,
        retryable: false,
      }

    case PG_ERROR.FOREIGN_KEY_VIOLATION:
      // A deliberate `raise ... using errcode = '23503'` says something useful;
      // only fall back to the generic wording for a real constraint trip. The
      // generic wording is also written for a DELETE — on an INSERT a 23503 means
      // a row this one POINTS AT is missing, which is the opposite situation.
      return (
        authoredMessage(err) ?? {
          message: 'This record is referenced by other entries and cannot be changed or removed.',
          code,
          retryable: false,
        }
      )

    case PG_ERROR.CHECK_VIOLATION:
    case PG_ERROR.NOT_NULL_VIOLATION:
      // Our RPCs raise their OWN messages on these codes — create_payment uses
      // `using errcode = '23514'` for "a payment needs a contact" and "a payment
      // amount must be greater than zero". Swallowing those into a generic
      // sentence is what left users staring at "Some values are missing or
      // invalid." with no idea which value.
      return (
        authoredMessage(err) ?? {
          message: 'Some values are missing or invalid.',
          code,
          retryable: false,
        }
      )

    case PG_ERROR.UNDEFINED_COLUMN:
    case PG_ERROR.UNDEFINED_FUNCTION:
    case PG_ERROR.UNDEFINED_TABLE:
      // Not retryable: the schema will not grow a column on its own, and
      // retrying just delays a message that names the actual fix.
      return {
        message:
          'This screen needs a database change that has not been applied yet. ' +
          'Run the pending migrations (supabase db push), then reload.',
        code,
        retryable: false,
      }

    case PG_ERROR.RAISE_EXCEPTION:
    case PG_ERROR.PROGRAM_LIMIT_EXCEEDED:
      // Our own triggers and RPCs raise these with messages written for the user
      // ("Cannot modify a posted voucher", "too many invitations in the last
      // hour"), so pass them straight through. Not retryable: waiting is the
      // remedy for a throttle, and an automatic retry would just burn it.
      return { message: err.message ?? 'That action is not allowed.', code, retryable: false }

    default:
      break
  }

  if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
    return { message: 'Could not reach the server. Try again.', code, retryable: true }
  }

  return { message: err.message ?? 'Something went wrong.', code, retryable: true }
}

/**
 * A user-facing Error that keeps the Postgres error `code` on it.
 *
 * Repositories normally throw `new Error(toAppError(e).message)`, which discards
 * the code — fine when the message is all the UI needs. Deletes are different:
 * the caller must tell a foreign-key block (offer "deactivate instead") from any
 * other failure, so those paths throw this instead and branch on `.code`.
 */
export class RepoError extends Error {
  code: string | undefined
  constructor(app: AppError) {
    super(app.message)
    this.name = 'RepoError'
    this.code = app.code
  }
}

/** Throw a code-preserving RepoError from a raw Supabase/PostgREST error. */
export function throwRepoError(error: unknown): never {
  throw new RepoError(toAppError(error))
}

/** True when an error is a foreign-key violation — i.e. the row is still referenced. */
export function isForeignKeyViolation(error: unknown): boolean {
  return (error as { code?: string })?.code === PG_ERROR.FOREIGN_KEY_VIOLATION
}
